import * as React from "react";
import type { ValueProducer } from "../../../../common/components/types";
import type {
    CurrentLocationListener} from "../../../../common/components/functions-glide";
import {
    stopListeningToCurrentLocation,
    listenToCurrentLocation,
} from "../../../../common/components/functions-glide";
import SwitchView from "../../../ui/views/switch/switch-view";
import { getLocalizedString } from "../../../../common/components/localization";
import { trackPlayerEvent } from "../../../../analytics";
import type { AppKind } from "common/components/app-kind";

interface LocationEntryProps {
    readonly validFlag: ValueProducer | undefined;
    readonly setValue: ((s: string) => void) | undefined;
    readonly disabled: boolean;
    readonly required: boolean;
    readonly appID: string;
    readonly appKind: AppKind;
}

export const LocationEntry: React.FunctionComponent<LocationEntryProps> = p => {
    // const [listening, setListening] = React.useState(false);
    const listening = React.useRef(false);
    const [currentValue, setCurrentValue] = React.useState<string | undefined>(undefined);

    const { setValue, validFlag, disabled, appID, required, appKind } = p;

    const isEnabled = !disabled && setValue !== undefined;

    const onGetLocation = React.useCallback(() => {
        const l: CurrentLocationListener = {
            updateLocation: (lat, long) => {
                if (listening.current) {
                    const v = `${lat},${long}`;
                    setValue?.(v);
                    setCurrentValue(v);
                    listening.current = false;
                    validFlag?.set(true);
                }
                stopListeningToCurrentLocation(l);
                trackPlayerEvent("location acquired", { app_id: appID, lat, long });
            },
            locationError: () => {
                if (listening.current) {
                    listening.current = false;
                    validFlag?.set(true);
                }
                stopListeningToCurrentLocation(l);
                trackPlayerEvent("location failed", { app_id: appID });
            },
        };
        listening.current = true;
        validFlag?.set(false);
        listenToCurrentLocation(l);
        return undefined;
    }, [setValue, validFlag, listening, appID]);

    const onRemoveLocation = React.useCallback(() => {
        if (setValue === undefined) {
            return;
        }
        setValue("");
        setCurrentValue(undefined);
        listening.current = false;
        validFlag?.set(true);
        return undefined;
    }, [setValue, validFlag]);

    if (currentValue !== undefined) {
        return (
            <SwitchView
                title={getLocalizedString("useMyCurrentLocation", appKind)}
                description={required ? getLocalizedString("required", appKind) : undefined}
                allowWrapping={true}
                visual="switch"
                onChanged={onRemoveLocation}
                isEnabled={isEnabled}
                checked={true}
            />
        );
    } else if (listening.current) {
        return (
            <SwitchView
                title={getLocalizedString("retrievingLocation", appKind)}
                description={required ? getLocalizedString("required", appKind) : undefined}
                allowWrapping={true}
                visual="switch"
                onChanged={onRemoveLocation}
                isEnabled={isEnabled}
                showSpinner={true}
                checked={true}
            />
        );
    } else {
        return (
            <SwitchView
                title={getLocalizedString("useMyCurrentLocation", appKind)}
                description={required ? getLocalizedString("required", appKind) : undefined}
                allowWrapping={true}
                visual="switch"
                onChanged={onGetLocation}
                isEnabled={isEnabled}
                checked={false}
            />
        );
    }
};
